import { useServiceCharges } from "@features/bx/BulkBillGeneration/hooks";
import { Badge } from "@shared/ui/badge";
import { Switch } from "@shared/ui/switch";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

interface ServiceChargesSubTabProps {
  value: Record<string, string[]>;
  onChange: (value: Record<string, string[]>) => void;
}

const CATEGORY_CONFIG: Record<string, { title: string, desc: string, colorClass: string, bgColorClass: string, dotClass: string, headerClass: string }> = {
  fixedCharges: {
    title: "Fixed Charges",
    desc: "Recurring flat fees applied every billing cycle regardless of consumption.",
    colorClass: "text-blue-700",
    bgColorClass: "bg-blue-50/50",
    dotClass: "bg-blue-500",
    headerClass: "bg-blue-50/30",
  },
  surcharges: {
    title: "Surcharges",
    desc: "Additional adjustments applied on top of base charges to recover variable costs.",
    colorClass: "text-amber-700",
    bgColorClass: "bg-amber-50/50",
    dotClass: "bg-amber-500",
    headerClass: "bg-amber-50/30",
  },
  taxes: {
    title: "Taxes",
    desc: "Government and local taxes applied to the bill.",
    colorClass: "text-purple-700",
    bgColorClass: "bg-purple-50/50",
    dotClass: "bg-purple-500",
    headerClass: "bg-purple-50/30",
  },
  penalties: {
    title: "Penalties",
    desc: "Charges applied for late payments, policy violations, or service incidents.",
    colorClass: "text-red-700",
    bgColorClass: "bg-red-50/50",
    dotClass: "bg-red-500",
    headerClass: "bg-red-50/30",
  }
};

const ServiceChargesSubTab = ({ value, onChange }: ServiceChargesSubTabProps) => {
  const { cycleId } = useParams();
  const { data: serviceChargesData } = useServiceCharges(cycleId || "");

  const { totalCharges, includedCount } = useMemo(() => {
    if (!serviceChargesData) return { totalCharges: 0, includedCount: 0 };
    let total = 0;
    let included = 0;
    
    Object.keys(CATEGORY_CONFIG).forEach((key) => {
      const arr = serviceChargesData[key];
      const snakeKey = key === "fixedCharges" ? "fixed_charges" : key;
      if (Array.isArray(arr)) {
        total += arr.length;
        included += (value[snakeKey] || []).length;
      }
    });

    return { totalCharges: total, includedCount: included };
  }, [serviceChargesData, value]);

  useEffect(() => {
    const isValueValidArrays = Object.values(value).some(v => Array.isArray(v));
    if (serviceChargesData && (Object.keys(value).length === 0 || (Object.keys(value).length > 0 && !isValueValidArrays))) {
      const initial: Record<string, string[]> = {};
      Object.keys(CATEGORY_CONFIG).forEach((key) => {
        const arr = serviceChargesData[key];
        const snakeKey = key === "fixedCharges" ? "fixed_charges" : key;
        if (Array.isArray(arr)) {
          initial[snakeKey] = arr.map((c: any) => c.code || c.name);
        }
      });
      onChange(initial);
    }
  }, [serviceChargesData]);

  const handleToggleCategory = (categoryKey: string, checked: boolean) => {
    const snakeKey = categoryKey === "fixedCharges" ? "fixed_charges" : categoryKey;
    const arr = serviceChargesData?.[categoryKey];
    if (Array.isArray(arr)) {
      const newArr = checked ? arr.map((c: any) => c.code || c.name) : [];
      onChange({ ...value, [snakeKey]: newArr });
    }
  };

  const categoriesToRender = ["fixedCharges", "surcharges", "taxes", "penalties"];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border-b pb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Service Charges Inclusion</h3>
          <p className="text-sm text-gray-500">Toggle individual charges or entire categories to include or exclude from this billing cycle.</p>
        </div>
        <div className="text-sm font-medium text-gray-500">
          <span className="text-blue-600 font-bold">{includedCount}</span> / {totalCharges} charges included
        </div>
      </div>

      {categoriesToRender.map(catKey => {
        const charges = serviceChargesData?.[catKey] || [];
        if (!charges.length) return null;

        const config = CATEGORY_CONFIG[catKey];
        const snakeKey = catKey === "fixedCharges" ? "fixed_charges" : catKey;
        const catIncludedCount = (value[snakeKey] || []).length;
        const catTotalCount = charges.length;
        const isAllIncluded = catTotalCount > 0 && catIncludedCount === catTotalCount;

        return (
          <div key={catKey} className="border rounded-xl overflow-hidden bg-white shadow-sm">
            {/* Category Header */}
            <div className={`p-4 flex items-center justify-between border-b ${config.headerClass}`}>
              <div className="flex items-start gap-3">
                <div className={`w-3 h-3 mt-1.5 rounded-full ${config.dotClass}`}></div>
                <div>
                  <h4 className={`font-bold text-base ${config.colorClass}`}>{config.title}</h4>
                  <p className="text-sm text-gray-600">{config.desc}</p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <span className={`text-sm font-semibold ${config.colorClass}`}>
                  {catIncludedCount} of {catTotalCount} included
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 font-medium">Include all</span>
                  <Switch 
                    checked={isAllIncluded} 
                    onCheckedChange={(checked) => handleToggleCategory(catKey, checked)} 
                  />
                </div>
              </div>
            </div>

            {/* List of charges */}
            <div className="bg-white">
              {charges.map((charge: any, idx: number) => {
                const id = charge.code || charge.name;
                const snakeKey = catKey === "fixedCharges" ? "fixed_charges" : catKey;
                const isIncluded = (value[snakeKey] || []).includes(id);

                return (
                  <div key={id} className="p-4 flex items-center justify-between border-b last:border-b-0 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-6">
                      <span className="text-gray-400 font-mono text-sm w-4 text-center">{idx + 1}</span>
                      <div>
                        <h5 className="font-semibold text-gray-900">{charge.name}</h5>
                        
                      </div>
                    </div>
                    <div className="flex items-center gap-8">
                      {charge.price && <span className="font-semibold text-gray-900">{charge.price}</span>}
                      <div className="flex items-center gap-3 w-[120px] justify-end">
                        <Badge 
                          variant={isIncluded ? "success" : "secondary"} 
                        >
                          {isIncluded ? "Included" : "Excluded"}
                        </Badge>
                        <Switch 
                          checked={isIncluded} 
                          onCheckedChange={(checked) => {
                            const newArr = checked 
                               ? [...(value[snakeKey] || []), id] 
                               : (value[snakeKey] || []).filter(v => v !== id);
                            onChange({ ...value, [snakeKey]: newArr });
                          }} 
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ServiceChargesSubTab;
